import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Clock, DollarSign, CheckCircle2, XCircle, FileText,
  Calendar, ArrowLeft, Loader2, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import SignaturePad from '../components/SignaturePad';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Timesheets({ onBack }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('timesheets');
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);

  useEffect(() => { fetchData(); }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tsRes, psRes] = await Promise.all([
        axios.get(`${API_URL}/api/timesheets/records?period=${period}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/payslips?period=${period}`, { withCredentials: true })
      ]);
      setTimesheets(tsRes.data);
      setPayslips(psRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateTimesheets = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/timesheets/generate`,
        { period }, { withCredentials: true });
      toast.success(`Generated ${data.count} timesheet(s)`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const approveTimesheet = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/timesheets/${id}/approve`,
        { status }, { withCredentials: true });
      toast.success(`Timesheet ${status}`);
      fetchData();
    } catch (err) {
      toast.error('Action failed');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="timesheets-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack || (() => navigate(-1))} className="text-muted-foreground" data-testid="timesheet-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-heading font-bold text-foreground">
            {isAdmin ? 'Timesheets & Payslips' : 'My Timesheets'}
          </h2>
          <p className="text-sm text-muted-foreground">Track hours worked and payments</p>
        </div>
        <div className="flex items-center gap-3">
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
            className="bg-muted border-border text-foreground w-40"
            data-testid="period-selector" />
          <Button variant="outline" className="border-border text-muted-foreground hover:bg-muted/50"
            onClick={() => window.open(`${API_URL}/api/export/timesheets/pdf?period=${period}`, '_blank')}
            data-testid="export-pdf-btn">
            <Download className="w-4 h-4 mr-2" />PDF
          </Button>
          <Button variant="outline" className="border-border text-muted-foreground hover:bg-muted/50"
            onClick={() => window.open(`${API_URL}/api/export/timesheets/csv?period=${period}`, '_blank')}
            data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />CSV
          </Button>
          {isAdmin && (
            <Button className="btn-primary" onClick={generateTimesheets} disabled={generating}
              data-testid="generate-timesheets-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              Generate
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        {['timesheets', 'payslips'].map((tab) => (
          <button key={tab}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors
              ${activeTab === tab ? 'text-[#FF6F61] border-b-2 border-[#FF6F61]' : 'text-muted-foreground hover:text-muted-foreground'}`}
            onClick={() => setActiveTab(tab)} data-testid={`tab-${tab}`}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : activeTab === 'timesheets' ? (
        <div className="space-y-4">
          {timesheets.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No timesheets for {period}</p>
              {isAdmin && <p className="text-xs text-muted-foreground mt-1">Click "Generate" to create timesheets from completed shifts</p>}
            </div>
          ) : (
            timesheets.map((ts) => (
              <Card key={ts.id} className="glass-card border-border">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base text-foreground">{ts.caregiver_name}</CardTitle>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      ts.status === 'approved' ? 'bg-[#A8E6CF]/20 text-[#A8E6CF]' :
                      ts.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                      'bg-amber-500/20 text-amber-400'
                    }`}>
                      {ts.status}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Period</p>
                      <p className="text-sm text-foreground">{ts.period}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Hours</p>
                      <p className="text-sm text-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />{ts.total_hours}h
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Pay</p>
                      <p className="text-sm text-[#FF6F61] font-semibold flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />{ts.total_pay?.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Shift entries */}
                  {ts.entries && ts.entries.length > 0 && (
                    <div className="space-y-1 mb-4">
                      <p className="text-xs text-muted-foreground mb-2">Shift Details:</p>
                      {ts.entries.map((e, i) => (
                        <div key={i} className="flex items-center justify-between text-xs p-2 rounded bg-muted">
                          <span className="text-muted-foreground">{e.date}</span>
                          <span className="text-foreground">{e.client_name}</span>
                          <span className="text-muted-foreground">{e.hours}h</span>
                          <span className="text-[#FF6F61]">${e.pay?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {isAdmin && ts.status === 'pending' && (
                    <div className="flex gap-2 mb-4">
                      <Button size="sm" className="bg-[#A8E6CF] hover:bg-[#6d8e68] text-white"
                        onClick={() => approveTimesheet(ts.id, 'approved')}
                        data-testid={`approve-ts-${ts.id}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" />Approve
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-500 text-red-400 hover:bg-red-500/10"
                        onClick={() => approveTimesheet(ts.id, 'rejected')}
                        data-testid={`reject-ts-${ts.id}`}>
                        <XCircle className="w-3 h-3 mr-1" />Reject
                      </Button>
                    </div>
                  )}

                  {/* Digital Signature */}
                  <SignaturePad
                    documentType="timesheet"
                    documentId={ts.id}
                    signerName={user?.name}
                  />
                </CardContent>
              </Card>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {payslips.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No payslips for {period}</p>
            </div>
          ) : (
            payslips.map((ps) => (
              <div key={ps.id} className="flex items-center justify-between p-4 rounded-lg bg-card border border-border"
                data-testid={`payslip-${ps.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#A8E6CF]/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-[#A8E6CF]" />
                  </div>
                  <div>
                    <p className="text-foreground font-medium">{ps.caregiver_name}</p>
                    <p className="text-xs text-muted-foreground">Period: {ps.period} | {ps.total_hours}h worked</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-[#FF6F61]">${ps.total_pay?.toFixed(2)}</p>
                  <p className="text-xs text-[#A8E6CF]">{ps.status}</p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
